class VariantSelector extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
  }

  connectedCallback() {
    this.storageKey = this.getStorageKey();
    this.addEventListener("change", this.handleChange);

    this.restoreSavedSelections();
    this.updateSelectedLabels();

    requestAnimationFrame(() => {
      this.notifyForm();

      const mediaId = this.getCheckedMediaId();
      if (mediaId) {
        const gallery = this.findGallerySwiper();
        gallery?.goToMediaId?.(mediaId);
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.handleChange);
  }

  getStorageKey() {
    const sectionId = this.dataset.sectionId || "default";
    const keyPart = this.dataset.key || this.getSelectorOptionNames().join("-");
    const productHandle =
      this.closest(".shopify-section")?.querySelector("product-form-section")
        ?.dataset.productHandle ||
      document.querySelector("product-form-section")?.dataset.productHandle ||
      window.location.pathname;

    return `variant-selector:${productHandle}:${sectionId}:${keyPart}`;
  }

  handleChange(event) {
    const target = event.target;
    if (!target.matches('input[type="radio"][data-option-value]')) return;

    this.saveSelections();
    this.updateSelectedLabels();
    this.notifyForm();

    const mediaId = target.dataset.mediaId;
    if (mediaId) {
      const gallery = this.findGallerySwiper();
      gallery?.goToMediaId?.(mediaId);
    }
  }

  getFieldsets() {
    return Array.from(this.querySelectorAll(".product-form__option"));
  }

  getSelections() {
    return this.getFieldsets()
      .map((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (!checked) return null;

        return {
          optionName: checked.name,
          value: checked.value,
        };
      })
      .filter(Boolean);
  }

  getSelectorOptionNames() {
    return this.getFieldsets()
      .map((fieldset) => {
        const input = fieldset.querySelector('input[type="radio"][name]');
        return input ? input.name : null;
      })
      .filter(Boolean);
  }

  hasAnySelection() {
    return this.getSelections().length > 0;
  }

  saveSelections() {
    try {
      const selections = this.getSelections();
      localStorage.setItem(this.storageKey, JSON.stringify(selections));
    } catch (error) {
      console.warn("Failed to save variant selector state", error);
    }
  }

  getCheckedMediaId() {
    const checked = this.querySelector(
      'input[type="radio"][data-option-value]:checked[data-media-id]',
    );
    return checked?.dataset.mediaId || null;
  }

  findGallerySwiper() {
    const root =
      this.closest(".product-form-wrap") ||
      this.closest(".shopify-section") ||
      this.closest("section") ||
      document;

    return (
      root.querySelector("swiper-slide") ||
      document.querySelector("swiper-slide")
    );
  }

  restoreSavedSelections() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;

      const selections = JSON.parse(raw);
      if (!Array.isArray(selections)) return;

      selections.forEach(({ optionName, value }) => {
        const escapedOptionName =
          window.CSS && CSS.escape ? CSS.escape(optionName) : optionName;
        const escapedValue =
          window.CSS && CSS.escape ? CSS.escape(value) : value;

        const input = this.querySelector(
          `input[type="radio"][name="${escapedOptionName}"][value="${escapedValue}"]`,
        );

        if (input && !input.disabled) {
          input.checked = true;
        }
      });
    } catch (error) {
      console.warn("Failed to restore variant selector state", error);
    }
  }

  updateSelectedLabels() {
    const fieldsets = this.getFieldsets();

    fieldsets.forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      const selectedLabel = fieldset.querySelector(
        ".product-form__selected-variant",
      );

      if (!selectedLabel) return;
      selectedLabel.textContent = checked ? ` ${checked.value}` : "";
    });
  }

  findProductFormSection() {
    const root =
      this.closest(".product-form-wrap") ||
      this.closest(".shopify-section") ||
      this.closest("section") ||
      this.parentElement;

    if (!root) {
      return document.querySelector("product-form-section");
    }

    return (
      root.querySelector("product-form-section") ||
      document.querySelector("product-form-section")
    );
  }

  notifyForm() {
    const productForm = this.findProductFormSection();
    if (!productForm) return;

    if (typeof productForm.syncFromAllVariantSelectors === "function") {
      productForm.syncFromAllVariantSelectors();
      return;
    }

    productForm.dispatchEvent(
      new CustomEvent("variant-selector:change", {
        bubbles: false,
      }),
    );
  }
}

customElements.define("variant-selector", VariantSelector);

class ProductFormSection extends HTMLElement {
  constructor() {
    super();

    this.product = null;
    this.selectedQuantity = 1;
    this.selectedVariants = {}; // { containerIndex: { optionIndex: value } }
    this.variantContainers = [];
    this.selectedVariantIds = [];
    this.availableVariants = new Map(); // "opt1::opt2::opt3" => variantId
    this.urlSelectedVariant = null;

    this.masterSelections = null; // { optionIndex: value }
    this.pendingExternalSelections = null;

    this.addBtn = null;
    this.addBtnOriginalText = "Add to cart";

    this.handleChange = this.handleChange.bind(this);
    this.handleAddToCart = this.addToCart.bind(this);
    this.handleExternalVariantSelectorChange =
      this.handleExternalVariantSelectorChange.bind(this);
  }

  connectedCallback() {
    this.cacheElements();
    this.selectedQuantity = this.getSelectedQtyFromHTML();
    this.loadURLState();
    this.bindEvents();
    this.initializeStaticUI();
    this.updateAddToCartState();
    this.loadProduct();
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.handleChange);
    this.removeEventListener(
      "variant-selector:change",
      this.handleExternalVariantSelectorChange,
    );
    this.addBtn?.removeEventListener("click", this.handleAddToCart);
  }

  cacheElements() {
    this.addBtn = this.querySelector(".add-to-cart");
    this.variantOptionsContainer = this.querySelector(".variant-options");
    this.packNameEl = this.querySelector(".product-form__pack-name");

    this.priceEl = document.querySelector(".details-prices__price");
    this.compareEl = document.querySelector(".details-prices__compare");
    this.savingEl = document.querySelector(".details-form__saved");

    if (this.addBtn) {
      this.addBtnOriginalText = this.addBtn.textContent || "Add to cart";
    }
  }

  bindEvents() {
    this.addEventListener("change", this.handleChange);
    this.addEventListener(
      "variant-selector:change",
      this.handleExternalVariantSelectorChange,
    );
    this.addBtn?.addEventListener("click", this.handleAddToCart);
  }

  findVariantSelectors() {
    const root =
      this.closest(".product-form-wrap") ||
      this.closest(".shopify-section") ||
      this.closest("section") ||
      this.parentElement;

    if (!root) {
      return Array.from(document.querySelectorAll("variant-selector"));
    }

    return Array.from(root.querySelectorAll("variant-selector"));
  }

  getCombinedVariantSelectorSelections() {
    const selectors = this.findVariantSelectors();
    const combined = [];

    selectors.forEach((selector) => {
      if (typeof selector.getSelections !== "function") return;

      const selections = selector.getSelections();
      selections.forEach((item) => combined.push(item));
    });

    return combined;
  }

  syncFromAllVariantSelectors() {
    const selections = this.getCombinedVariantSelectorSelections();
    if (!selections.length) return;

    this.applyExternalVariantSelections(selections);
  }

  handleChange(event) {
    const target = event.target;

    if (target.name === "qty") {
      this.handleQuantityInputChange(target);
      return;
    }

    if (target.name === "variant-select") {
      this.handleVariantInputChange(target);
    }
  }

  handleExternalVariantSelectorChange() {
    this.syncFromAllVariantSelectors();
  }

  applyExternalVariantSelections(selections) {
    if (!Array.isArray(selections) || !selections.length) return;

    if (!this.product) {
      this.pendingExternalSelections = selections;
      return;
    }

    const normalizedSelections = this.normalizeExternalSelections(selections);
    if (!normalizedSelections) return;

    const mergedMasterSelections = {
      ...(this.masterSelections || {}),
      ...normalizedSelections,
    };

    this.masterSelections = mergedMasterSelections;

    for (
      let containerIndex = 0;
      containerIndex < this.selectedQuantity;
      containerIndex++
    ) {
      this.selectedVariants[containerIndex] = {
        ...(this.selectedVariants[containerIndex] || {}),
        ...mergedMasterSelections,
      };
    }

    this.renderVariantUI();
    this.updateSelectedVariantIds();
    this.updateURL();
    this.updateAddToCartState();
  }

  findVariantSelector() {
    const root =
      this.closest(".product-form-wrap") ||
      this.closest(".shopify-section") ||
      this.closest("section") ||
      this.parentElement;

    if (!root) {
      return document.querySelector("variant-selector");
    }

    return (
      root.querySelector("variant-selector") ||
      document.querySelector("variant-selector")
    );
  }

  applyInitialVariantSelectorState() {
    this.syncFromAllVariantSelectors();
  }

  normalizeExternalSelections(selections) {
    if (!this.product?.options?.length) return null;

    const normalized = {};

    this.product.options.forEach((option, optionIndex) => {
      const match = selections.find((item) => item.optionName === option.name);
      if (!match) return;

      normalized[optionIndex] = match.value;
    });

    return normalized;
  }

  handleQuantityInputChange(input) {
    this.selectedQuantity = parseInt(input.value, 10);
    this.renderSavings(input);
    this.renderPackName(input);
    this.renderPrice(input);

    this.syncSelectionsToQuantity();

    this.renderVariantUI();
    this.updateURL();
    this.updateAddToCartState();
  }

  syncSelectionsToQuantity() {
    const nextSelections = {};

    for (
      let containerIndex = 0;
      containerIndex < this.selectedQuantity;
      containerIndex++
    ) {
      if (this.selectedVariants[containerIndex]) {
        nextSelections[containerIndex] = {
          ...this.selectedVariants[containerIndex],
        };
      } else if (this.masterSelections) {
        nextSelections[containerIndex] = { ...this.masterSelections };
      } else {
        nextSelections[containerIndex] = {};
      }
    }

    this.selectedVariants = nextSelections;
  }

  handleVariantInputChange(select) {
    this.updateSelectedVariantValue(select);

    const wrap = select.closest(".el-color-select");
    if (wrap) {
      const swatch = wrap.querySelector(".el-color-swatch");
      if (swatch) this.updateSwatchFromSelect(select, swatch);
    }

    this.updateAddToCartState();
  }

  initializeStaticUI() {
    const checkedQtyInput = this.querySelector('input[name="qty"]:checked');
    this.renderSavings(checkedQtyInput);
    this.renderPrice(checkedQtyInput);
    this.renderPackName(checkedQtyInput);
  }

  getSelectedQtyFromHTML() {
    return parseInt(
      this.querySelector('input[name="qty"]:checked')?.value || 1,
      10,
    );
  }

  getColorSwatches() {
    const raw = this.dataset.colorSwatch || "";
    return raw ? raw.split("_") : [];
  }

  loadURLState() {
    const params = new URLSearchParams(window.location.search);
    this.urlSelectedVariant = params.get("variant");
  }

  async loadProduct() {
    try {
      const handle =
        this.dataset.productHandle || window.location.pathname.split("/").pop();

      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error("Failed to fetch product");

      this.product = await response.json();

      this.cacheAvailableVariants();
      this.selectedQuantity = this.getSelectedQtyFromHTML();

      this.renderVariantUI();
      this.applyURLSelections();

      if (this.pendingExternalSelections?.length) {
        this.applyExternalVariantSelections(this.pendingExternalSelections);
        this.pendingExternalSelections = null;
      } else {
        this.applyInitialVariantSelectorState();
      }

      this.updateAddToCartState();
    } catch (error) {
      console.error("Failed to load product", error);
    }
  }

  cacheAvailableVariants() {
    this.availableVariants.clear();

    this.product?.variants?.forEach((variant) => {
      if (!variant.available) return;
      this.availableVariants.set(variant.options.join("::"), variant.id);
    });
  }

  applyURLSelections() {
    if (!this.urlSelectedVariant || !this.product) return;

    const ids = String(this.urlSelectedVariant).split(",");

    ids.forEach((id, containerIndex) => {
      const variant = this.product.variants.find(
        (item) => String(item.id) === String(id),
      );

      if (!variant) return;

      this.selectedVariants[containerIndex] = {};
      variant.options.forEach((optionValue, optionIndex) => {
        this.selectedVariants[containerIndex][optionIndex] = optionValue;
      });
    });

    this.updateUISelections();
    this.updateURL();
    this.updateAddToCartState();
  }

  updateURL() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    if (this.selectedVariantIds.length) {
      params.set(
        "variant",
        this.selectedQuantity > 1
          ? this.selectedVariantIds.join(",")
          : this.selectedVariantIds[0],
      );
    } else {
      params.delete("variant");
    }

    url.search = params.toString();
    history.replaceState({}, "", url.toString());
  }

  renderPrice(input) {
    const priceStr = input?.dataset?.bundlePrice;
    const compareStr = input?.dataset?.bundleCompare;

    if (!this.priceEl || !this.compareEl || !priceStr) return;

    this.priceEl.textContent = priceStr;

    const savings = this.calculateSavings(input);
    if (savings != null && compareStr) {
      this.compareEl.textContent = compareStr;
    }
  }

  renderPackName(input) {
    if (!this.packNameEl) return;
    this.packNameEl.textContent = input?.dataset?.optionName || "";
  }

  renderSavings(input) {
    if (!this.savingEl) return;

    const savings = this.calculateSavings(input);

    if (savings != null) {
      this.savingEl.textContent = `${savings} OFF`;
    } else {
      this.savingEl.remove();
    }
  }

  calculateSavings(input) {
    const priceStr = input?.dataset?.bundlePrice;
    const compareStr = input?.dataset?.bundleCompare;

    if (!priceStr || !compareStr) return null;

    const cleanPriceStr = priceStr.replace(/,/g, "");
    const cleanCompareStr = compareStr.replace(/,/g, "");
    const symbol = this.getCurrencySymbol(cleanPriceStr);

    const price = parseFloat(cleanPriceStr.replace(symbol, ""));
    const compare = parseFloat(cleanCompareStr.replace(symbol, ""));
    const savings = compare - price;

    if (!isFinite(savings) || savings <= 0) return null;
    return `${symbol}${+savings.toFixed(2)}`;
  }

  getCurrencySymbol(value) {
    const match = String(value || "").match(/^[^\d.]+/);
    return match ? match[0] : "";
  }

  updateSelectedVariantValue(select) {
    const containerIndex = Number(select.dataset.containerIndex);
    const optionIndex = Number(select.dataset.optionIndex);

    if (!this.selectedVariants[containerIndex]) {
      this.selectedVariants[containerIndex] = {};
    }

    if (select.value === "") {
      delete this.selectedVariants[containerIndex][optionIndex];

      for (let i = optionIndex + 1; i < this.product.options.length; i++) {
        delete this.selectedVariants[containerIndex][i];
      }
    } else {
      this.selectedVariants[containerIndex][optionIndex] = select.value;
    }

    this.updateDependentSelects(containerIndex, optionIndex);
    this.updateSelectedVariantIds();
    this.updateURL();
  }

  isAnyAvailableVariantMatching(selections) {
    if (!this.product?.variants) return false;

    const definedSelections = Object.entries(selections).filter(
      ([, value]) => value !== undefined && value !== "",
    );

    if (!definedSelections.length) return true;

    return this.product.variants.some((variant) => {
      if (!variant.available) return false;

      return definedSelections.every(([index, value]) => {
        return variant.options[Number(index)] === value;
      });
    });
  }

  getAvailableOptions(currentSelections, optionIndex) {
    const optionValues = this.product.options[optionIndex].values;

    for (let i = 0; i < optionIndex; i++) {
      if (!currentSelections[i]) return [...optionValues];
    }

    return optionValues.filter((value) => {
      const nextSelections = { ...currentSelections, [optionIndex]: value };
      return this.isAnyAvailableVariantMatching(nextSelections);
    });
  }

  updateDependentSelects(containerIndex, changedOptionIndex) {
    const container = this.variantContainers[containerIndex];
    if (!container) return;

    const currentSelections = {
      ...(this.selectedVariants[containerIndex] || {}),
    };

    for (
      let optionIndex = changedOptionIndex + 1;
      optionIndex < this.product.options.length;
      optionIndex++
    ) {
      const select = container.querySelector(
        `[data-option-index="${optionIndex}"]`,
      );

      if (!select) continue;

      const previousValue = select.value;
      const availableOptions = this.getAvailableOptions(
        currentSelections,
        optionIndex,
      );

      this.populateSelect(select, optionIndex, availableOptions);

      if (previousValue && availableOptions.includes(previousValue)) {
        select.value = previousValue;
        currentSelections[optionIndex] = previousValue;
      } else {
        select.value = "";
        delete currentSelections[optionIndex];
      }

      const wrap = select.closest(".el-color-select");
      if (wrap) {
        const swatch = wrap.querySelector(".el-color-swatch");
        if (swatch) this.updateSwatchFromSelect(select, swatch);
      }
    }

    this.selectedVariants[containerIndex] = currentSelections;
  }

  populateSelect(select, optionIndex, availableOptions) {
    const option = this.product.options[optionIndex];
    const optionName = option.name;
    const colorSwatches = this.getColorSwatches();

    const optionsHtml = [
      `<option value="">Select ${optionName}</option>`,
      ...option.values.map((value, index) => {
        const isAvailable = availableOptions.includes(value);
        const disabledAttr = isAvailable ? "" : "disabled";
        const selectedAttr = select.value === value ? "selected" : "";
        const isColor = optionName === "Color";
        const color = isColor ? colorSwatches[index] : "";
        const swatchAttr = isColor && color ? ` data-swatch="${color}"` : "";
        const swatchSpan =
          isColor && color
            ? ` <span class="el-color-swatch" style="background:${color}"></span>`
            : "";
        const label = isAvailable ? value : `${value} - Sold Out`;

        return `<option value="${value}" ${disabledAttr} ${selectedAttr}${swatchAttr}>${swatchSpan}${label}</option>`;
      }),
    ];

    select.innerHTML = optionsHtml.join("");
  }

  updateSelectedVariantIds() {
    if (!this.product) {
      this.selectedVariantIds = [];
      return;
    }

    this.selectedVariantIds = Array.from(
      { length: this.selectedQuantity },
      (_, containerIndex) => {
        const picks = this.product.options.map(
          (_, optionIndex) =>
            this.selectedVariants[containerIndex]?.[optionIndex],
        );

        if (picks.some((value) => !value)) return null;

        const key = picks.join("::");
        return this.availableVariants.get(key) || null;
      },
    ).filter(Boolean);
  }

  isSelectionComplete() {
    return (
      !!this.product && this.selectedVariantIds.length === this.selectedQuantity
    );
  }

  updateAddToCartState() {
    if (!this.addBtn) return;

    this.updateSelectedVariantIds();

    const isComplete = this.isSelectionComplete();
    this.addBtn.disabled = !isComplete;
    this.addBtn.textContent = isComplete
      ? this.addBtnOriginalText
      : this.selectedQuantity > 1
        ? "Select all options"
        : "Select options";
  }

  renderVariantUI() {
    if (!this.variantOptionsContainer || !this.product) return;

    this.variantOptionsContainer.innerHTML = "";
    this.variantContainers = [];

    for (
      let containerIndex = 0;
      containerIndex < this.selectedQuantity;
      containerIndex++
    ) {
      const variantContainer = this.buildVariantContainer(containerIndex);
      this.variantContainers.push(variantContainer);
      this.variantOptionsContainer.appendChild(variantContainer);
    }

    this.updateSelectedVariantIds();
    this.updateAddToCartState();
  }

  buildVariantContainer(containerIndex) {
    const container = document.createElement("div");
    container.className = "variant-container";
    container.innerHTML = `<h4>#${containerIndex + 1}</h4>`;

    const selectsContainer = document.createElement("div");
    selectsContainer.className = "variant-selects-container";

    const currentSelections =
      this.selectedVariants[containerIndex] || this.getDefaultSelections();

    if (!this.selectedVariants[containerIndex]) {
      this.selectedVariants[containerIndex] = { ...currentSelections };
    }

    this.product.options.forEach((option, optionIndex) => {
      const select = this.buildVariantSelect(
        containerIndex,
        optionIndex,
        option,
        currentSelections,
      );

      if (option.name === "Color") {
        selectsContainer.appendChild(this.buildColorSelectWrapper(select));
      } else {
        selectsContainer.appendChild(select);
      }
    });

    container.appendChild(selectsContainer);
    return container;
  }

  getDefaultSelections() {
    return this.masterSelections ? { ...this.masterSelections } : {};
  }

  buildVariantSelect(containerIndex, optionIndex, option, currentSelections) {
    const select = document.createElement("select");
    select.name = "variant-select";
    select.className = "variant-select";
    select.dataset.containerIndex = containerIndex;
    select.dataset.optionIndex = optionIndex;

    const availableOptions = this.getAvailableOptions(
      currentSelections,
      optionIndex,
    );

    this.populateSelect(select, optionIndex, availableOptions);

    if (currentSelections[optionIndex]) {
      select.value = currentSelections[optionIndex];
    }

    return select;
  }

  buildColorSelectWrapper(select) {
    const wrap = document.createElement("div");
    wrap.className = "el-color-select";

    const swatch = document.createElement("span");
    swatch.className = "el-color-swatch";
    swatch.setAttribute("aria-hidden", "true");

    this.updateSwatchFromSelect(select, swatch);

    wrap.appendChild(swatch);
    wrap.appendChild(select);

    return wrap;
  }

  updateUISelections() {
    Object.entries(this.selectedVariants).forEach(
      ([containerIndex, options]) => {
        Object.entries(options).forEach(([optionIndex, value]) => {
          const select = this.querySelector(
            `select[data-container-index="${containerIndex}"][data-option-index="${optionIndex}"]`,
          );

          if (!select) return;

          select.value = value;
          this.updateDependentSelects(
            Number(containerIndex),
            Number(optionIndex),
          );

          const wrap = select.closest(".el-color-select");
          if (wrap) {
            const swatch = wrap.querySelector(".el-color-swatch");
            if (swatch) this.updateSwatchFromSelect(select, swatch);
          }
        });
      },
    );

    this.updateSelectedVariantIds();
    this.updateAddToCartState();
  }

  updateSwatchFromSelect(select, swatchEl) {
    const selectedOption = select.selectedOptions?.[0];
    const color = selectedOption?.dataset?.swatch;

    if (color) {
      swatchEl.style.backgroundColor = color;
      swatchEl.style.display = "block";
    } else {
      swatchEl.style.backgroundColor = "transparent";
      swatchEl.style.display = "none";
    }
  }

  async addToCart() {
    this.updateSelectedVariantIds();

    if (!this.isSelectionComplete()) {
      this.updateAddToCartState();
      return;
    }

    try {
      this.setAddButtonState("Adding...", true);

      const payload = {
        items: this.selectedVariantIds.map((id) => ({ id, quantity: 1 })),
        sections: "el-custom-cart-drawer,el-header",
      };

      const response = await fetch(
        `${window.Shopify?.routes?.root || "/"}cart/add.js`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to add items to cart");
      }

      const data = await response.json();
      this.openCartDrawer();
      this.emitCartEvents(data);
    } catch (error) {
      console.error(error);
      alert("Failed to add items. Please try again.");
    } finally {
      this.updateAddToCartState();
    }
  }

  setAddButtonState(label, disabled) {
    if (!this.addBtn) return;
    this.addBtn.textContent = label;
    this.addBtn.disabled = disabled;
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector("custom-cart-drawer");
    if (cartDrawer) cartDrawer.setAttribute("open", "");
  }

  emitCartEvents(data) {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:rerender", {
        detail: data,
        bubbles: true,
      }),
    );

    this.dispatchEvent(
      new CustomEvent("cart-updated", {
        bubbles: true,
      }),
    );
  }
}

customElements.define("product-form-section", ProductFormSection);
